import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { getUserHomes, Home } from "../services/home.api";
import { useAuthStore } from "../store/authStore";
import { toast } from "sonner";

export interface Property {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  role?: 'owner' | 'member' | 'viewer';
  /** null = owner (full access); string[] = family member's granted service types */
  grantedServiceTypes?: string[] | null;
  /** 'owner' or 'family_member' */
  accessRole?: 'owner' | 'family_member';
  /** Name of the primary home owner (null for owned homes) */
  ownerName?: string | null;
  assetCount?: number;
  issueCount?: number;
  maintenanceCount?: number;
}

interface PropertyContextType {
  currentProperty: Property | null;
  properties: Property[];
  switchProperty: (propertyId: string) => void;
  refreshProperties: () => Promise<void>;
  isLoading: boolean;
  /** Returns true if current property allows this service (owners always true) */
  hasServiceAccess: (serviceType: string) => boolean;
}

const PropertyContext = createContext<PropertyContextType | undefined>(
  undefined
);

const SELECTED_PROPERTY_KEY = 'selectedPropertyId';

export function PropertyProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthStore();
  const [properties, setProperties] = useState<Property[]>([]);
  const [currentProperty, setCurrentProperty] = useState<Property | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load properties from API
  const loadProperties = async () => {
    if (!user?.id) {
      setProperties([]);
      setCurrentProperty(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const homes = await getUserHomes();
      
      // getUserHomes returns Home[] directly (already unwrapped)
      const homesList = Array.isArray(homes) ? homes : [];
      
      // Convert Home[] to Property[]
      const propertyList: Property[] = homesList.map((home: Home) => ({
        id: home.id,
        name: home.name || `${home.city} Home`,
        address: home.address,
        city: home.city,
        state: home.state,
        zip: home.zip,
        country: home.country,
        role: home.role,
        grantedServiceTypes: home.grantedServiceTypes,
        accessRole: home.accessRole,
        ownerName: home.ownerName,
        assetCount: home.assetCount,
        issueCount: home.issueCount,
        maintenanceCount: home.maintenanceCount,
      }));

      setProperties(propertyList);

      // Restore previously selected property or select first one
      const savedPropertyId = localStorage.getItem(SELECTED_PROPERTY_KEY);
      const selectedProperty = savedPropertyId 
        ? propertyList.find(p => p.id === savedPropertyId)
        : propertyList[0];

      setCurrentProperty(selectedProperty || propertyList[0] || null);
    } catch (error) {
      console.error('Failed to load properties:', error);
      setProperties([]);
      setCurrentProperty(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Load properties on mount and when user changes
  useEffect(() => {
    loadProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const switchProperty = (propertyId: string) => {
    const property = properties.find((p) => p.id === propertyId);
    if (property) {
      setCurrentProperty(property);
      localStorage.setItem(SELECTED_PROPERTY_KEY, propertyId);
      toast.success(`Switched to ${property.name}`);
    }
  };

  const refreshProperties = async () => {
    await loadProperties();
  };

  /** 
   * null grantedServiceTypes = owner = all services allowed
   * No properties = new user = allow all (pages show empty states)
   */
  const hasServiceAccess = (serviceType: string): boolean => {
    if (!currentProperty) return true; // No properties yet - allow access to all pages
    if (currentProperty.grantedServiceTypes === null || currentProperty.grantedServiceTypes === undefined) return true;
    return currentProperty.grantedServiceTypes.includes(serviceType);
  };

  return (
    <PropertyContext.Provider
      value={{ 
        currentProperty, 
        properties, 
        switchProperty, 
        refreshProperties,
        isLoading,
        hasServiceAccess,
      }}
    >
      {children}
    </PropertyContext.Provider>
  );
}

export function useProperty() {
  const context = useContext(PropertyContext);
  if (context === undefined) {
    throw new Error("useProperty must be used within a PropertyProvider");
  }
  return context;
}
